// CORE
import React, { useState, useCallback, useMemo } from 'react';

import { Form, Formik } from 'formik';
import { List as ImmutableList } from 'immutable';
import { useAppDispatch, useAppSelector } from 'hooks/redux';
import { noop } from 'lodash';
import type { SetOptional } from 'type-fest';

import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import Drawer from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import Radio from '@mui/material/Radio';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { alpha } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/ClearRounded';
import AppleWalletLogo from 'assets/logos/apple.svg';
import { getDrawerSx } from 'components/Drawers/drawerStyles';
import { makeStyles } from 'tss-react/mui';
import ArrowDropDownRoundedIcon from '@mui/icons-material/ArrowDropDownRounded';

// DATA
import { accountsActions, accountsSelectors, accountsTypes, accountsUtils } from 'app-components/flux/accounts';
import type {
  AccountSubType,
  AccountType,
  AccountTypeNode,
  BaseAccountProps,
  UsageType,
} from 'app-components/flux/accounts/accountsTypes';
import { businessActions, businessSelectors } from 'app-components/flux/business';
import { entitlementsSelectors } from 'app-components/flux/entitlements';
import { institutionLoginsSelectors } from 'app-components/flux/institution-logins';
import { useFetchOnce } from 'app-components/hooks/utils';
import { rootUiActions } from 'app-components/flux/root-ui';
import { tracker } from 'app-components/utils/core';
import usePrivacyMaskValue from 'app-components/hooks/privacyMode/usePrivacyMaskValue';

// LOCAL
import AccountTypeSelector from 'components/Formik/AccountTypeSelector';
import AmountField from 'components/QuickenControls/AmountField';
import Dump from 'components/Dump';
import InfoTooltipIcon from 'components/InfoTooltipIcon';
import PercentageField from 'components/PercentageField';
import InstitutionIcon from 'components/InstitutionIcon';
import LinkPreview from 'components/LinkPreview';
import QButton from 'components/QButton';

import UsageAndEntitySelector from '../UsageAndEntitySelector';

export const DIALOG_TYPE_ACCOUNT_EDIT = 'DIALOG_TYPE_ACCOUNT_EDIT';

const useStyles = makeStyles()(({ palette, spacing, breakpoints, shadows }) => ({
  drawer: {
    width: '100%',
    flexShrink: 0,
    [breakpoints.up('sm')]: {
      // For using the web app on mobile
      width: '50%',
      maxWidth: 520,
    },
  },
  drawerPaper: {
    maxHeight: '100%',
    width: '100%',
    overflow: 'hidden',
    [breakpoints.up('sm')]: {
      width: '50%',
      maxWidth: 520,
    },
  },
  container: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
  },
  header: {
    display: 'flex',
    flex: 0,
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: `${spacing(2)} ${spacing(4)}`,
    borderBottom: `1px solid ${palette.border.main}`,
  },
  content: {
    flex: 1,
    overflow: 'auto',
    padding: `${spacing(3)} ${spacing(4)}`,
    display: 'flex',
    flexDirection: 'column',
    gap: spacing(2.5),
  },
  accountHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: spacing(1.5),
  },
  appleLogo: {
    width: 48,
    height: 48,
    borderRadius: '50%',
  },
  footer: {
    position: 'sticky',
    bottom: 0,
    borderTop: `1px solid ${palette.border.main}`,
    backgroundColor: palette.background.paper,
    display: 'flex',
    flexDirection: 'row',
    width: '100%',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: spacing(2),
    padding: `${spacing(3)} ${spacing(3)} ${spacing(4)} ${spacing(3)}`,
  },
  tooltip: {
    maxWidth: '150px',
  },
  notice: {
    marginTop: 10,
    display: 'flex',
    padding: '7px 0px 7px 0px',
    borderRadius: 12,
    backgroundColor: palette.action.hover,
  },
  captionText: {
    margin: 'auto',
  },
  tooltipPlacement: {
    marginBottom: -1,
    marginLeft: 5,
  },
  balanceLabelRow: {
    display: 'flex',
    alignItems: 'center',
    marginTop: spacing(1),
    marginBottom: spacing(1.5),
  },
  panelChoice: {
    display: 'flex',
    alignItems: 'center',
    gap: spacing(2),
    padding: spacing(2),
    borderRadius: 12,
    cursor: 'pointer',
    backgroundColor: palette.background.paper,
    border: '1px solid transparent',
    boxShadow: shadows[3],
    '& + &': {
      marginTop: spacing(1.5),
    },
    '&:hover': {
      borderColor: palette.primary.main,
      boxShadow: `0px 0px 0px 3px ${alpha(palette.primary.main, 0.04)}`,
    },
  },
  panelChoiceSelected: {
    borderColor: palette.primary.main,
    backgroundImage: `linear-gradient(90deg, ${alpha(palette.primary.main, 0.04)} 0%, ${alpha(palette.primary.main, 0.04)} 100%), linear-gradient(90deg, ${palette.background.paper} 0%, ${palette.background.paper} 100%)`,
    boxShadow: `0px 0px 0px 3px ${alpha(palette.primary.main, 0.04)}`,
  },
  panelTitleRow: {
    display: 'flex',
    alignItems: 'center',
    gap: spacing(1),
    marginBottom: spacing(0.25),
  },
  closeButton: {
    backgroundColor: palette.background.default,
    color: palette.icon.main,
  },
  accordion: {
    margin: 0,
    '&:before': {
      height: 0,
    },
  },
  accordionSummary: {
    padding: 0,
    backgroundColor: palette.background.paper,
    flexDirection: 'row-reverse',
    '& .MuiAccordionSummary-expandIconWrapper': {
      transform: 'rotate(-90deg)',
    },
    '& .MuiAccordionSummary-expandIconWrapper.Mui-expanded': {
      transform: 'rotate(0deg)',
    },
    borderColor: 'transparent',
    '.Mui-expanded': {
      margin: 0,
    },
    '&.Mui-expanded': {
      minHeight: 48,
    },
  },
  accordionDetails: {
    padding: 0,
  },
}));

type FormSubmitAccountType = { type: AccountType; subType?: AccountSubType };

function AccountEditDialog(props: {
  accountId: string;
  dialogId: string;
  focusField?: 'interestRate' | 'creditLimit';
}) {
  const { accountId, dialogId, focusField } = props;

  const dispatch = useAppDispatch();
  const { classes } = useStyles();

  const businessEnabled = useAppSelector(entitlementsSelectors.getBusinessEnabled);
  const businessesById = useAppSelector(businessSelectors.getResourcesById)?.filter((business) => !business.isClosed);
  const defaultBusiness = useAppSelector(businessSelectors.getDefaultBusiness);
  useFetchOnce(businessActions.getBusiness, businessSelectors);

  const account = useAppSelector((state) => accountsSelectors.getAccountById(state, accountId));
  const isManualAccount = accountsUtils.isAccountManual(account);
  const isStripeAccount = accountsUtils.isStripeAccount(account);
  const originalAccountType = account?.type ?? 'UNKNOWN';
  const isARAccount = originalAccountType === 'ACCOUNTS_RECEIVABLE';

  const institutionLogin = useAppSelector((state) =>
    account?.institutionLoginId
      ? institutionLoginsSelectors.getInstitutionLoginForId(state, account.institutionLoginId)
      : undefined,
  );
  const institution = useAppSelector((state) =>
    institutionLoginsSelectors.getInstitutionForInstitutionLogin(state, institutionLogin?.id || ''),
  );

  const isZillowAccount = institutionLogin?.channel === 'ZILLOW';
  const isKbbAccount = institutionLogin?.channel === 'KELLEY_BLUE_BOOK';
  const isAppleWallet = institutionLogin?.channel === 'APPLE_WALLET';
  const fiName = institutionLogin?.name || 'Manual account';
  const lastFour = (accountsUtils.formatFinalFour(account) || '').slice(-4);
  const accountSubtitle = lastFour ? `${fiName} •••${lastFour}` : fiName;

  const actualNormalizeOnlineBalance = account?.aggregators?.first?.()?.normalizeOnlineBalance;
  const interestRateData = useAppSelector((state) => accountsSelectors.getAccountInterestRate(state, accountId));
  const creditLimitData = useAppSelector((state) => accountsSelectors.getAccountCreditLimit(state, accountId));
  const privacyMask = usePrivacyMaskValue();

  // ====================================== INIT State Values =============================================== //
  const initialManualInterestRate = account?.interestRate?.toString() ?? '';
  const initialManualCreditLimit = account?.creditLimit as number | undefined;

  // ====================================== Component State ================================================ //
  const [updatedAccountName, setUpdatedAccountName] = useState<string | null>(null);
  const [updatedAccountType, setUpdatedAccountType] = useState<FormSubmitAccountType | null>(null);
  const [updatedUsageType, setUpdatedUsageType] = useState<UsageType | null>(null);
  const [normalizeOnlineBalance, setNormalizeOnlineBalance] = useState(actualNormalizeOnlineBalance);
  const [manualInterestRate, setManualInterestRate] = useState<string>(initialManualInterestRate ?? '');
  const [manualCreditLimit, setManualCreditLimit] = useState<number | undefined>(initialManualCreditLimit);

  // ====================================== Redux Selectors ================================================ //
  const accountsById = useAppSelector(accountsSelectors.getAccountsById);

  // ====================================== Component State ─ open/close ============================= //
  // Setting isOpen to false triggers MUI's slide-out transition; we remove from Redux only after
  // the animation completes (MUI leavingScreen default = 195 ms).
  const [isOpen, setIsOpen] = useState(true);

  const effectiveSubType = updatedAccountType?.subType ?? account?.subType;
  const isCreditCardType = effectiveSubType === 'CREDIT_CARD';
  const canShowCreditCardFields = isCreditCardType && accountsUtils.canEditCreditCardInformation(account);

  // ====================================== Callbacks ================================================ //
  const handleClose = useCallback(() => {
    setIsOpen(false);
    setTimeout(() => dispatch(rootUiActions.removeDialog(dialogId)), 225);
  }, [dispatch, dialogId]);

  const handleUpdateAccount = useCallback(
    (type: AccountType, params = {}) => {
      if (account?.id) {
        const options = { id: account.id, ...params };
        dispatch(
          accountsActions.updateAccount(accountsTypes.mkUpdateAccount(type, options), {
            undo: { userMessage: 'Account updated' },
          }),
        );
      }
    },
    [account, dispatch],
  );

  const handleUpdateAccountNameType = () => {
    handleClose();
    const params: SetOptional<BaseAccountProps, 'type'> = {};
    if (updatedAccountName) {
      params.name = updatedAccountName;
    }
    if (updatedAccountType) {
      params.subType = updatedAccountType.subType;
    }

    if (updatedUsageType) {
      if (updatedUsageType !== 'PERSONAL') {
        tracker.track('accountConnectedToBusiness');
        params.businessId = updatedUsageType;
        params.usageType = 'BUSINESS';
      } else {
        params.usageType = 'PERSONAL';
      }
    }
    const aggregator = account?.aggregators?.first?.();
    if (aggregator && Boolean(aggregator.normalizeOnlineBalance) !== Boolean(normalizeOnlineBalance)) {
      params.aggregators = ImmutableList([
        aggregator.merge({
          normalizeOnlineBalance,
        }),
      ]);
    }
    if (isCreditCardType) {
      if (interestRateData.isManual) {
        params.interestRate = manualInterestRate ? parseFloat(manualInterestRate) : -1;
      }
      if (creditLimitData.isManual) {
        params.creditLimit = manualCreditLimit ?? -1;
      }
    }
    handleUpdateAccount(updatedAccountType?.type || originalAccountType, params);
  };

  const updateAccountType = useCallback(({ type, usage }: { type: AccountTypeNode; usage: UsageType }) => {
    setUpdatedAccountType({ type: type.qcsType, subType: type.qcsSubType });
    setUpdatedUsageType(usage);
  }, []);

  const handleInterestRateBlur = useCallback(() => {
    if (manualInterestRate) {
      const num = parseFloat(manualInterestRate);
      setManualInterestRate(isNaN(num) ? '' : num.toFixed(2));
    }
  }, [manualInterestRate]);

  const handleCreditLimitChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const parsed = parseFloat(e.target.value);
    setManualCreditLimit(e.target.value && !isNaN(parsed) ? Math.abs(parsed) : undefined);
  }, []);

  const nameAtInstitution = account?.aggregators?.get(0)?.accountName;

  const updatedAccountNameErrorMessage = useMemo(() => {
    if (updatedAccountName === '') return 'Account name cannot be empty';
    if (
      accountsById
        .filter((x) => x.name !== account?.name)
        .some((x) => x?.name?.toLowerCase() === updatedAccountName?.toLowerCase())
    )
      return 'Account name must be unique';
    if (accountsUtils.isInvalidName(updatedAccountName ?? '')) return 'Invalid account name.';
    return null;
  }, [updatedAccountName, accountsById, account]);

  const updatedAccountNameHelperText = useMemo(() => {
    if (updatedAccountNameErrorMessage) return updatedAccountNameErrorMessage;
    return nameAtInstitution ? <span>Name at institution: {nameAtInstitution}</span> : '';
  }, [updatedAccountNameErrorMessage, nameAtInstitution]);

  const initialUsage = (account?.usageType === 'BUSINESS' && (account.businessId || defaultBusiness?.id)) || 'PERSONAL';

  const accountTypeForm = useMemo(() => {
    return (
      <Formik
        initialValues={{
          type: accountsTypes.getAccountTypeNodeByQcsType(originalAccountType, account?.subType),
          usage: initialUsage,
        }}
        validateOnChange
        validateOnBlur={false}
        validate={updateAccountType}
        onSubmit={noop}
      >
        <Form>
          <AccountTypeSelector
            currentAccountType={accountsTypes.getAccountTypeNodeByQcsType(originalAccountType, account?.subType)}
            showManualTypes={isManualAccount}
            sx={{ marginTop: 2, width: '100%' }}
            TextFieldProps={{
              id: 'account_type',
              label: 'Type',
              style: { width: '100%' },
              readOnly: isStripeAccount || isARAccount || isZillowAccount || isKbbAccount,
            }}
            name='type'
            disabled={isARAccount}
          />

          {businessEnabled && businessesById?.size > 0 && (
            <Tooltip
              arrow
              placement='right-end'
              title={isStripeAccount ? 'This business must match the one associated with your payment account.' : null}
              classes={{ tooltip: classes.tooltip }}
            >
              <Box sx={{ width: '100%' }}>
                <UsageAndEntitySelector
                  businessesById={businessesById}
                  name='usage'
                  id='usage_type'
                  label='Account Purpose'
                  variant='filled'
                  margin='normal'
                  size='normal'
                  readOnly={isStripeAccount}
                  sx={{
                    flex: 1,
                    '& .MuiSelect-select': {
                      display: 'flex',
                    },
                    flexGrow: 1,
                    width: '100%',
                  }}
                  disabled={isARAccount}
                />
              </Box>
            </Tooltip>
          )}
        </Form>
      </Formik>
    );
  }, [
    originalAccountType,
    account?.subType,
    initialUsage,
    updateAccountType,
    isManualAccount,
    isStripeAccount,
    businessEnabled,
    businessesById,
    classes.tooltip,
    isZillowAccount,
    isKbbAccount,
    isARAccount,
  ]);

  return (
    <Drawer
      anchor='right'
      open={isOpen}
      onClose={handleClose}
      className={classes.drawer}
      classes={{ paper: classes.drawerPaper }}
      sx={getDrawerSx({
        '& .MuiDrawer-paper': {
          maxWidth: { sm: 520 },
        },
      })}
      PaperProps={{ id: 'edit-account-drawer' }}
    >
      <Box className={classes.container}>
        <Box className={classes.header}>
          <Typography variant='titleMedium'>Edit account</Typography>
          <IconButton className={classes.closeButton} size='small' aria-label='Close' onClick={handleClose}>
            <CloseIcon />
          </IconButton>
        </Box>

        <Box className={classes.content}>
          <Dump
            obj={{
              accountId,
              dialogId,
              actualNormalizeOnlineBalance,
              normalizeOnlineBalance,
              updatedAccountName,
              updatedAccountType,
              account,
            }}
          />

          <Box className={classes.accountHeader}>
            {isAppleWallet ? (
              <img src={AppleWalletLogo} alt='Apple Wallet logo' className={classes.appleLogo} />
            ) : account?.id ? (
              <Box sx={{ '&:empty': { display: 'none' } }}>
                <InstitutionIcon accountId={account.id} size='sm' showEmptyInstitutionIcon={false} />
              </Box>
            ) : null}
            <Stack gap={0.25} minWidth={0} sx={{ flex: 1 }}>
              <Typography variant='titleMedium' noWrap>
                {accountSubtitle}
              </Typography>
              {institution?.url ? (
                <Typography variant='paragraphRegular' color='textSecondary' noWrap>
                  <LinkPreview href={institution.url} showFavicon={false} />
                </Typography>
              ) : null}
            </Stack>
          </Box>

          <div style={{ width: '100%' }}>
            <TextField
              label='Name'
              style={{ width: '100%' }}
              variant='filled'
              defaultValue={account?.name}
              onChange={(e) => setUpdatedAccountName(e.target.value)}
              error={updatedAccountNameErrorMessage !== null}
              helperText={updatedAccountNameHelperText}
              id='edit_account_name_field'
            />

            {accountTypeForm}

            {canShowCreditCardFields && (
              <>
                {interestRateData.isManual ? (
                  <PercentageField
                    id='edit_account_interest_rate_field'
                    label='Interest rate'
                    style={{ width: '100%', marginTop: 24 }}
                    variant='filled'
                    autoFocus={focusField === 'interestRate'}
                    value={manualInterestRate}
                    onChange={setManualInterestRate}
                    onBlur={handleInterestRateBlur}
                    usePrivacyMode
                    endAdornment={
                      <InfoTooltipIcon
                        size='small-no-padding'
                        tooltipText='Add your purchase interest rate to track it in Simplifi. Look for it on your statement or by logging into your account.'
                      />
                    }
                  />
                ) : interestRateData.value != null ? (
                  <Box
                    sx={{
                      mt: 3,
                      border: '1px solid',
                      borderColor: 'border.light',
                      borderRadius: '12px',
                      px: 2,
                      py: 1,
                      height: 58,
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'center',
                      bgcolor: 'background.paper',
                    }}
                  >
                    <Typography variant='captionRegular' color='text.secondary'>
                      Interest rate
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant='paragraphRegular'>{privacyMask(`${interestRateData.value}%`)}</Typography>
                    </Box>
                  </Box>
                ) : null}

                {creditLimitData.isManual ? (
                  <AmountField
                    label='Credit limit'
                    style={{ width: '100%', marginTop: 24 }}
                    textFieldVariant='filled'
                    autoFocus={focusField === 'creditLimit'}
                    editable
                    showAmountAdornment
                    amountType='creditLimit'
                    fractionDigits={2}
                    value={manualCreditLimit}
                    onChange={handleCreditLimitChange}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position='end'>
                          <InfoTooltipIcon
                            size='small-no-padding'
                            tooltipText="Add to track your card's credit usage in real-time. Look for it on your statement or by logging into your account."
                          />
                        </InputAdornment>
                      ),
                    }}
                  />
                ) : creditLimitData.value != null ? (
                  <Box
                    sx={{
                      mt: 3,
                      border: '1px solid',
                      borderColor: 'border.light',
                      borderRadius: '12px',
                      px: 2,
                      py: 1,
                      height: 58,
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'center',
                      bgcolor: 'background.paper',
                    }}
                  >
                    <Typography variant='captionRegular' color='text.secondary'>
                      Credit limit
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant='paragraphRegular'>
                        {privacyMask(`$${creditLimitData.value.toLocaleString()}`)}
                      </Typography>
                    </Box>
                  </Box>
                ) : null}
              </>
            )}

            {['BANK', 'CREDIT'].includes(updatedAccountType?.type || originalAccountType) &&
              account?.aggregators?.size &&
              account.aggregators.size > 0 && (
                <Box marginTop={2}>
                  <Divider sx={{ marginTop: 3, marginBottom: 1 }} />

                  <Accordion data-testid='edit-account-balance-resolution' elevation={0} className={classes.accordion}>
                    <AccordionSummary expandIcon={<ArrowDropDownRoundedIcon />} className={classes.accordionSummary}>
                      <Typography variant='paragraphRegular'>Advanced Options</Typography>
                    </AccordionSummary>

                    <AccordionDetails className={classes.accordionDetails}>
                      <div className={classes.balanceLabelRow}>
                        <Typography variant='paragraphMedium'>Balance Resolution</Typography>
                        <InfoTooltipIcon
                          className={classes.tooltipPlacement}
                          size='small-no-padding'
                          tooltipText='If your balance looks incorrect, try selecting “Not included” (some banks already include pending transactions in the balance they share with us, causing double counting).'
                        />
                      </div>

                      <Typography variant='paragraphRegular' sx={{ marginBottom: 1.5 }}>
                        Select how your bank handles pending transactions.
                      </Typography>

                      <Box
                        className={`${classes.panelChoice} ${!normalizeOnlineBalance ? classes.panelChoiceSelected : ''}`}
                        onClick={() => setNormalizeOnlineBalance(false)}
                        role='radio'
                        aria-checked={!normalizeOnlineBalance}
                      >
                        <Radio checked={!normalizeOnlineBalance} size='small' sx={{ p: 0 }} />
                        <Box sx={{ flex: 1 }}>
                          <div className={classes.panelTitleRow}>
                            <Typography variant='paragraphMedium'>Does not include them in balance</Typography>
                            <Chip
                              label='Default'
                              variant='outlined'
                              size='small'
                              sx={{
                                height: 22,
                                borderColor: 'secondary.light',
                                borderRadius: '8px',
                                color: 'primary.dark',
                                '& .MuiChip-label': { px: 1, py: 0, fontSize: '0.75rem' },
                              }}
                            />
                          </div>
                          <Typography variant='paragraphRegular' sx={{ color: 'text.secondary' }}>
                            Your bank sends a pending-free online balance.
                          </Typography>
                          <Typography variant='paragraphRegular' sx={{ color: 'text.secondary' }}>
                            {`Quicken won't adjust what your bank sends us.`}
                          </Typography>
                        </Box>
                      </Box>

                      <Box
                        className={`${classes.panelChoice} ${normalizeOnlineBalance ? classes.panelChoiceSelected : ''}`}
                        onClick={() => setNormalizeOnlineBalance(true)}
                        role='radio'
                        aria-checked={!!normalizeOnlineBalance}
                      >
                        <Radio checked={!!normalizeOnlineBalance} size='small' sx={{ p: 0 }} />
                        <Box sx={{ flex: 1 }}>
                          <Typography variant='paragraphMedium'>Includes them in balance</Typography>
                          <Typography variant='paragraphRegular' sx={{ color: 'text.secondary' }}>
                            Quicken will adjust your bank balance based on pending transactions to prevent-double
                            counting.
                          </Typography>
                        </Box>
                      </Box>

                      {Boolean(account?.aggregators?.first?.()?.normalizeOnlineBalance) !==
                        Boolean(normalizeOnlineBalance) && (
                        <div className={classes.notice}>
                          <Typography variant='captionRegular' className={classes.captionText}>
                            This will take affect after the next successful balance refresh.
                          </Typography>
                        </div>
                      )}
                    </AccordionDetails>
                  </Accordion>
                </Box>
              )}
          </div>
        </Box>

        <Box className={classes.footer}>
          <QButton id='edit_account_cancel_button' variant='outlined' onClick={handleClose}>
            Cancel
          </QButton>

          <QButton
            variant='contained'
            disabled={
              ((!updatedAccountName || updatedAccountName === account?.name) &&
                !updatedAccountType &&
                Boolean(actualNormalizeOnlineBalance) === Boolean(normalizeOnlineBalance) &&
                !(
                  isCreditCardType &&
                  ((interestRateData.isManual && manualInterestRate !== initialManualInterestRate) ||
                    (creditLimitData.isManual && manualCreditLimit !== initialManualCreditLimit))
                )) ||
              updatedAccountNameErrorMessage !== null
            }
            onClick={handleUpdateAccountNameType}
            id='edit_account_update_button'
          >
            Update
          </QButton>
        </Box>
      </Box>
    </Drawer>
  );
}

export default AccountEditDialog;
